import { useEffect, useState } from 'react'

import { useMutationRedeemAccessCode } from '@/api/access-codes/mutations'
import { useQueryRedemptionStatus } from '@/api/access-codes/queries'
import { ACCESS_CODE_QUERY_KEYS } from '@/api/access-codes/keys'
import { AccessContext, RedeemAccessCodeResponse, RedemptionStatus } from '@/api/access-codes/types'

import { queryClient } from '@/config/react-query'
import { useAuth } from '@/hooks/useAuth'
import { Logger } from '@/lib/logger'

import AccessGateLocked from './AccessGateLocked'
import AccessGatePending from './AccessGatePending'
import AccessGateApproved from './AccessGateApproved'
import AccessGateRejected from './AccessGateRejected'

const logger = new Logger('ChargingPointAccessGate')

const DECISION_POLL_INTERVAL = 5000

interface ChargingPointAccessGateProps {
  context: AccessContext
  chargingPointId: number
}

const ChargingPointAccessGate: React.FC<ChargingPointAccessGateProps> = ({ context, chargingPointId }) => {
  const { refreshUser } = useAuth()
  const [code, setCode] = useState('')
  const [redemption, setRedemption] = useState<RedeemAccessCodeResponse | null>(null)

  const [pendingRedemptionId, setPendingRedemptionId] = useState<number | null>(context.pending_redemption_id)

  const { error, isPending, mutate, reset } = useMutationRedeemAccessCode({
    onSuccess: async (data) => {
      logger.success('Access code redeemed successfully: ', data)

      setRedemption(data)

      if (data.status === 'PENDING') {
        setPendingRedemptionId(data.redemption_id ?? null)
      }

      if (data.status === 'APPROVED') {
        refreshUser()
        await queryClient.invalidateQueries({ queryKey: [ACCESS_CODE_QUERY_KEYS.ACCESS_CONTEXT] })
      }
    },
    onError: (error) => {
      logger.error('Error redeeming access code: ', error)
    },
  })

  const isAwaitingDecision = !!pendingRedemptionId || redemption?.status === 'PENDING'

  const { data: decision } = useQueryRedemptionStatus(
    { redemptionId: pendingRedemptionId! },
    {
      enabled: !!pendingRedemptionId,
      refetchInterval: (query) =>
        query.state.data && query.state.data.status !== 'PENDING' ? false : DECISION_POLL_INTERVAL,
      refetchIntervalInBackground: false,
    }
  )

  const decidedStatus: RedemptionStatus | undefined =
    decision && decision.status !== 'PENDING' ? decision.status : undefined

  useEffect(() => {
    if (decidedStatus !== 'APPROVED') {
      return
    }

    logger.success('Access approved by the manager')

    refreshUser()
    queryClient.invalidateQueries({ queryKey: [ACCESS_CODE_QUERY_KEYS.ACCESS_CONTEXT] })
  }, [decidedStatus, refreshUser])

  const handleCodeChange = (value: string) => {
    setCode(value)

    if (error) {
      reset()
    }
  }

  const handleRedeem = () => {
    if (!code.trim() || isPending) {
      return
    }

    logger.info('Redeeming access code: ', code)

    mutate({
      code: code.trim().toUpperCase(),
      charging_point_id: chargingPointId,
    })
  }

  const handleUseAnotherCode = () => {
    setPendingRedemptionId(null)
    setRedemption(null)
    setCode('')
    reset()
  }

  if (decidedStatus === 'REJECTED') {
    return (
      <AccessGateRejected
        entityName={context.entity_name}
        message={decision?.message}
        onTryAnotherCode={handleUseAnotherCode}
      />
    )
  }

  if (redemption?.status === 'APPROVED' || decidedStatus === 'APPROVED') {
    return <AccessGateApproved entityName={context.entity_name} message={redemption?.message ?? decision?.message} />
  }

  if (isAwaitingDecision) {
    return (
      <AccessGatePending
        entityName={context.entity_name}
        message={redemption?.message}
        onUseAnotherCode={handleUseAnotherCode}
      />
    )
  }

  return (
    <AccessGateLocked
      context={context}
      code={code}
      isPending={isPending}
      error={error}
      onCodeChange={handleCodeChange}
      onSubmit={handleRedeem}
    />
  )
}

export default ChargingPointAccessGate
